import { coreApi } from '$api'
import {
  PayClassification,
  PayClassificationRequest,
  transformPayClassificationOnGet,
} from '$models'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'
import sortBy from 'lodash/sortBy'

/**
 * Get all Pay Classifications
 * @returns Promise<PayClassification[]>
 */
export const getAll = async (): Promise<PayClassification[]> => {
  const { hasTimePermission, hasPayrollPermission } = getActiveCompanySettings()

  if (!hasTimePermission && !hasPayrollPermission) return []

  const response = await singletonQueryClient.fetchQuery({
    queryKey: ['getAllPayClassifications'],
    queryFn: async () => {
      return await coreApi.getPayClassificationList()
    },
  })

  const result = response.map((pc) => transformPayClassificationOnGet(pc))
  return sortBy<PayClassification>(result, 'name')
}

/**
 * Get a Pay Classification by ID
 * @param payClassificationId the ID of the pay classification to get
 * @returns the pay classificaion with the given ID
 */
export const getPayClassification = async (
  payClassificationId: string
): Promise<PayClassification | null> => {
  const { hasTimePermission, hasPayrollPermission } = getActiveCompanySettings()

  if (!hasTimePermission && !hasPayrollPermission) return null

  const response = await singletonQueryClient.fetchQuery({
    queryKey: ['getPayClassification', payClassificationId],
    queryFn: async () => {
      return await coreApi.getPayClassification(payClassificationId)
    },
  })
  return transformPayClassificationOnGet(response)
}

/**
 * Creates/adds a new pay classification
 * @param payClassification the pay classification to add/create
 * @returns the newly created pay classification
 */
export const addPayClassification = async (
  payClassification: PayClassification
): Promise<PayClassification | null> => {
  const { hasTimePermission, hasPayrollPermission } = getActiveCompanySettings()

  if (!hasTimePermission && !hasPayrollPermission) return null

  const newPayClassification = await coreApi.postPayClassification(
    payClassification as PayClassificationRequest
  )
  return transformPayClassificationOnGet(newPayClassification)
}

/**
 * Updates an existing pay classification
 * @param payClassification the pay classification to update
 * @returns the updated pay classification
 */
export const updatePayClassification = async (
  payClassification: PayClassification
): Promise<PayClassification | null> => {
  const { hasTimePermission, hasPayrollPermission } = getActiveCompanySettings()

  if (!hasTimePermission && !hasPayrollPermission) return null

  const updatedPayClassification = await coreApi.putPayClassification(
    payClassification.id!,
    payClassification as PayClassificationRequest
  )
  return transformPayClassificationOnGet(updatedPayClassification)
}
